import type { SSRData, Client, Exchange, Operation } from '@urql/core'
import {
  createClient,
  ssrExchange,
  fetchExchange,
  cacheExchange,
} from '@urql/core'
import { ref } from 'vue'
import { pipe, tap } from 'wonka'

const ssrKey = '__URQL_DATA__'

export const LivePreviewExchange = (): Exchange => {
  return ({ client, forward }) =>
    (ops$) => {
      if (typeof window === 'undefined') {
        return forward(ops$)
      }

      const watchedOperations = new Map<number, Operation>()
      const observedOperations = new Map<number, number>()

      window.addEventListener('message', async ({ data }) => {
        if (!data || data.name !== 'statamic.preview.updated') return

        watchedOperations.forEach((op) => {
          client.reexecuteOperation(
            client.createRequestOperation('query', op, {
              ...op.context,
              requestPolicy: 'cache-and-network',
            }),
          )
        })
      })

      const processIncomingOperation = (op: Operation) => {
        if (op.kind === 'query' && !observedOperations.has(op.key)) {
          observedOperations.set(op.key, 1)
          watchedOperations.set(op.key, op)
        }

        if (op.kind === 'teardown' && observedOperations.has(op.key)) {
          observedOperations.delete(op.key)
          watchedOperations.delete(op.key)
        }
      }

      return forward(pipe(ops$, tap(processIncomingOperation)))
    }
}

export default defineNuxtPlugin((nuxt) => {
  const config = useRuntimeConfig()
  if (!config.public.GQL_HOST) {
    throw new Error('GQL_HOST is not defined in runtime config')
  }

  const ssr = ssrExchange({
    isClient: import.meta.client,
  })
  if (import.meta.client) {
    nuxt.hook('app:created', () => {
      ssr.restoreData(nuxt.payload[ssrKey] as SSRData)
    })
  }
  if (import.meta.server) {
    nuxt.hook('app:rendered', () => {
      nuxt.payload[ssrKey] = ssr.extractData()
    })
  }

  // @ts-expect-error: $router has no type definition
  const { query } = nuxt.$router.currentRoute.value

  const exchanges = [cacheExchange, ssr, fetchExchange]
  if (query.token) exchanges.unshift(LivePreviewExchange())

  const client = createClient({
    url: config.public.GQL_HOST + (query.token ? `?token=${query.token}` : ''),
    exchanges,
    fetchOptions: () => ({ credentials: 'include' }),
  })

  nuxt.provide('urql', client)
  nuxt.vueApp.provide('$urql', ref(client))
})

declare module '#app' {
  interface NuxtApp {
    $urql: Client
  }
}
